<template>
  <div>
    <div class="panel-heading">
      <h4 class="panel-title">
        <i class="material-icons rtl-mr-6" style="vertical-align: middle; font-size: 20px;">dashboard</i>
        {{ $t('adminDashboard.title') }}
        <small v-if="lastUpdated" style="font-size: 11px; color: #aaa;" class="rtl-ml-10">{{ $t('adminDashboard.updatedAtLabel', { time: lastUpdated }) }}</small>
        <button @click="loadAll" class="btn btn-xs btn-default pull-right" style="margin-top: 2px;" :disabled="loading">
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i> {{ $t('adminDashboard.refreshButton') }}
        </button>
      </h4>
    </div>
    <div class="panel-body" style="display: block;">

      <!-- Loading state -->
      <div v-if="loading" class="text-center" style="padding: 40px 0;">
        <i class="fa fa-spinner fa-spin fa-2x text-muted"></i>
        <div class="text-muted" style="margin-top: 8px;">{{ $t('adminDashboard.loadingDashboardText') }}</div>
      </div>

      <div v-else>

        <!-- ORDER INTAKE -->
        <div class="dash-section-title">{{ $t('adminDashboard.orderIntakeSection') }}</div>
        <div class="row" style="margin-bottom: 10px;">
          <div
            v-for="stat in intakeStats"
            :key="stat.key"
            class="col-xs-6 col-sm-4 col-md-3"
            style="margin-bottom: 10px;"
          >
            <router-link :to="stat.route" tag="div" class="dash-stat-box" :style="{ backgroundColor: stat.color }">
              <div class="dash-stat-inner">
                <i class="material-icons dash-stat-icon">{{ stat.icon }}</i>
                <div class="dash-stat-count">{{ orderCounts[stat.key] || 0 }}</div>
                <div class="dash-stat-label">{{ stat.label }}</div>
                <div class="dash-stat-amount">${{ formatAmount(orderCounts[stat.key + '_amount']) }}</div>
              </div>
            </router-link>
          </div>
        </div>

        <!-- PROCESSING -->
        <div class="dash-section-title">{{ $t('adminDashboard.processingSection') }}</div>
        <div class="row" style="margin-bottom: 10px;">
          <div
            v-for="stat in processingStats"
            :key="stat.key"
            class="col-xs-6 col-sm-4 col-md-3"
            style="margin-bottom: 10px;"
          >
            <router-link :to="stat.route" tag="div" class="dash-stat-box" :style="{ backgroundColor: stat.color }">
              <div class="dash-stat-inner">
                <i class="material-icons dash-stat-icon">{{ stat.icon }}</i>
                <div class="dash-stat-count">{{ orderCounts[stat.key] || 0 }}</div>
                <div class="dash-stat-label">{{ stat.label }}</div>
                <div class="dash-stat-amount">${{ formatAmount(orderCounts[stat.key + '_amount']) }}</div>
              </div>
            </router-link>
          </div>
        </div>

        <!-- FULFILLMENT -->
        <div class="dash-section-title">{{ $t('adminDashboard.fulfillmentSection') }}</div>
        <div class="row" style="margin-bottom: 10px;">
          <div
            v-for="stat in fulfillmentStats"
            :key="stat.key"
            class="col-xs-6 col-sm-4 col-md-3"
            style="margin-bottom: 10px;"
          >
            <router-link :to="stat.route" tag="div" class="dash-stat-box" :style="{ backgroundColor: stat.color }">
              <div class="dash-stat-inner">
                <i class="material-icons dash-stat-icon">{{ stat.icon }}</i>
                <div class="dash-stat-count">{{ orderCounts[stat.key] || 0 }}</div>
                <div class="dash-stat-label">{{ stat.label }}</div>
                <div class="dash-stat-amount">${{ formatAmount(orderCounts[stat.key + '_amount']) }}</div>
              </div>
            </router-link>
          </div>
        </div>

        <!-- DELIVERY & COMPLETION -->
        <div class="dash-section-title">{{ $t('adminDashboard.deliveryCompletionSection') }}</div>
        <div class="row" style="margin-bottom: 10px;">
          <div
            v-for="stat in deliveryStats"
            :key="stat.key"
            class="col-xs-6 col-sm-4 col-md-3"
            style="margin-bottom: 10px;"
          >
            <router-link :to="stat.route" tag="div" class="dash-stat-box" :style="{ backgroundColor: stat.color }">
              <div class="dash-stat-inner">
                <i class="material-icons dash-stat-icon">{{ stat.icon }}</i>
                <div class="dash-stat-count">{{ orderCounts[stat.key] || 0 }}</div>
                <div class="dash-stat-label">{{ stat.label }}</div>
                <div class="dash-stat-amount">${{ formatAmount(orderCounts[stat.key + '_amount']) }}</div>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  data() {
    return {
      loading: true,
      orderCounts: {},
      capital: null,
      lastUpdated: ''
    }
  },

  computed: {
    intakeStats() {
      return [
        { key: 'all_orders', label: this.$t('orders.allOrdersTitle'), color: '#1565c0', icon: 'list_alt', route: '/all-order' },
        { key: 'new_order', label: this.$t('orders.newOrdersTitle'), color: '#2e7d32', icon: 'fiber_new', route: '/new-order' },
        { key: 'ship_only', label: this.$t('columns.shipOnly'), color: '#00838f', icon: 'local_shipping', route: '/ship-only' }
      ]
    },
    processingStats() {
      return [
        { key: 'pending_answer', label: this.$t('adminDashboard.pendingAnswerLabel'), color: '#e65100', icon: 'help_outline', route: '/pending-answer-order' },
        { key: 'processed_order', label: this.$t('adminDashboard.processedLabel'), color: '#546e7a', icon: 'settings', route: '/processed_order' },
        { key: 'approved_order', label: this.$t('customer.approvedTitle'), color: '#00695c', icon: 'check_circle', route: '/approved_order' },
        { key: 'canceled', label: this.$t('common.canceledStatus'), color: '#c62828', icon: 'cancel', route: '/canceled_order' },
        { key: 'refunded', label: this.$t('common.refundedStatus'), color: '#4527a0', icon: 'undo', route: '/refunded_order' },
        { key: 'partial_refund', label: this.$t('finance.statusPartialRefunded'), color: '#6a1b9a', icon: 'money_off', route: '/secret?component=partial-refund' }
      ]
    },
    fulfillmentStats() {
      return [
        { key: 'purchased', label: this.$t('adminDashboard.purchasedLabel'), color: '#558b2f', icon: 'shopping_cart', route: '/purchased_order' },
        { key: 'warehouse_delivered_order', label: this.$t('adminDashboard.warehouseDeliveredLabel'), color: '#827717', icon: 'move_to_inbox', route: '/warehouse_delivered_order' },
        { key: 'pending_to_shipment', label: this.$t('orders.pendingShipmentTooltip'), color: '#37474f', icon: 'schedule', route: '/pending_to_shipment_order' },
        { key: 'int_shipment', label: this.$t('adminDashboard.intShipmentLabel'), color: '#0277bd', icon: 'flight', route: '/int_shipment_order' }
      ]
    },
    deliveryStats() {
      return [
        { key: 'pending_to_branch', label: this.$t('adminDashboard.pendingToBranchLabel'), color: '#4e342e', icon: 'store', route: '/pending_to_branch_order' },
        { key: 'branch_delivery', label: this.$t('adminDashboard.branchDeliveryLabel'), color: '#bf360c', icon: 'local_mall', route: '/branch_delivery_order' },
        { key: 'delivered', label: this.$t('adminDashboard.deliveredLabel'), color: '#1b5e20', icon: 'done_all', route: '/user-delivered-order' }
        // Archived card hidden per request (2026-09-17):
        // { key: 'archived', label: this.$t('customer.archivedTitle'), color: '#263238', icon: 'archive', route: '/admin-archived-order' }
      ]
    },
    financialStats() {
      if (!this.capital) return []
      return [
        { label: this.$t('adminDashboard.kurdistanBalance'), value: this.capital.kurBalance, color: '#01579b', icon: 'account_balance', route: '/banks' },
        { label: this.$t('adminDashboard.usaBalance'), value: this.capital.usaBalance, color: '#0d47a1', icon: 'account_balance', route: '/banks' },
        { label: this.$t('adminDashboard.customersOweUs'), value: this.capital.ourLoanOnCustomer, color: '#e65100', icon: 'people', route: '/regular-user' },
        { label: this.$t('adminDashboard.weOweCustomers'), value: Math.abs(this.capital.customerLoanOnUs || 0), color: '#880e4f', icon: 'people_outline', route: '/regular-user' },
        { label: this.$t('adminDashboard.supplierLoan'), value: this.capital.supplierLoanOnUs, color: '#4a148c', icon: 'business', route: '/secret?component=capital' },
        { label: this.$t('adminDashboard.totalCapital'), value: this.capital.capital, color: '#1b5e20', icon: 'monetization_on', route: '/secret?component=capital' }
      ]
    }
  },

  mounted() {
    const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
    if (!userData.is_staff) {
      this.$router.push('/')
      return
    }
    this.loadAll()
  },

  methods: {
    loadAll() {
      this.loading = true
      const token = localStorage.getItem('access_token')
      const headers = { Authorization: 'Bearer ' + token }

      axios.get('api/order-status-counts', { headers })
        .then(response => {
          this.orderCounts = response.data
        })
        .catch(() => {})
        .then(() => {
          if (this.$_has_perm('view_capital')) {
            return axios.get('api/capital', { headers })
              .then(response => {
                this.capital = response.data
              })
              .catch(() => {})
          }
        })
        .then(() => {
          this.loading = false
          this.lastUpdated = new Date().toLocaleTimeString()
        })
    },

    formatNumber(val) {
      if (val === null || val === undefined) return '—'
      return Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    formatAmount(val) {
      if (val === null || val === undefined || val === 0) return '0'
      return Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    }
  }
}
</script>

<style scoped>
.dash-section-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 5px;
  margin-bottom: 10px;
  margin-top: 18px;
}
.dash-section-title:first-child {
  margin-top: 0;
}
.dash-stat-box {
  border-radius: 6px;
  color: #fff;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  display: block;
  min-height: 90px;
}
.dash-stat-box:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  color: #fff;
  text-decoration: none;
}
.dash-stat-inner {
  padding: 14px 14px 12px 14px;
  position: relative;
  overflow: hidden;
}
.dash-stat-icon {
  position: absolute;
  top: 8px;
  right: 10px;
  font-size: 38px;
  opacity: 0.22;
}
.dash-stat-count {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
}
.dash-stat-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 4px;
  opacity: 0.9;
}
.dash-stat-amount {
  font-size: 12px;
  font-weight: 600;
  margin-top: 4px;
  opacity: 0.85;
  letter-spacing: 0.2px;
}
</style>



// WEBPACK FOOTER //
// src/components/adminDashboard.vue